// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useId } from 'react';
import * as styles from '../styles/SkillProfile.styles';
import { FeedbackWidget } from './FeedbackWidget';
import { Box, Button, Typography, Container, Paper, Stack, Chip, Alert } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PendingIcon from '@mui/icons-material/Pending';

export type SkillProfileAssessment = {
  is_completed?: number;
  assessedLevel: number; // 1-5
  levelLabel: string;
  overallScore: number; // Overall score percentage (0-100)
  levelScores?: { level: number; score: number; percentage: number }[];
  keyStrengthsSummary?: string;
  keyStrengthsHeader?: string[];
  keyStrengths: string[];
  conceptGapsSummary?: string;
  conceptGapsHeader?: string[];
  conceptGaps: string[];
  generatedAt: string;
};

const CheckIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const TrendIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TargetIcon = (): JSX.Element => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

const StarIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const StarFull = ({ size = 24 }: { size?: number }): JSX.Element => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const StarHalf = ({ size = 24, clipPathId }: { size?: number; clipPathId: string }): JSX.Element => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path
      fill="currentColor"
      clipPath={clipPathId ? `url(#${clipPathId})` : undefined}
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
    />
    <path
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
    />
  </svg>
);

const StarEmpty = ({ size = 24 }: { size?: number }): JSX.Element => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinejoin="round"
  >
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

/** Map assessedLevel (1–5) to 5 stars: full or empty per star. */
function getStarStates(level: number): ('full' | 'half' | 'empty')[] {
  const clamped = Math.max(1, Math.min(5, Math.floor(level)));
  const states: ('full' | 'half' | 'empty')[] = [];
  for (let i = 0; i < 5; i++) {
    states.push(i < clamped ? 'full' : 'empty');
  }
  return states;
}

export interface SkillProfileViewProps {
  assessment: SkillProfileAssessment | undefined;
  loading?: boolean;
  error?: string;
  /** Dimension to be Assessed selected on screen 1 (Assessment Initiation). */
  dimensionToBeAssessed?: string;
  onViewLearningPaths?: () => void;
  onStartNewInterview?: () => void;
  serverCallId?: string;
  userEmail?: string;
  onGoBack?: () => void;
}

export const SkillProfileView = ({
  assessment,
  loading,
  error,
  dimensionToBeAssessed,
  onViewLearningPaths,
  onStartNewInterview,
  serverCallId,
  userEmail,
  onGoBack
}: SkillProfileViewProps): JSX.Element => {
  if (loading) {
    return <div style={styles.loadingMessage}>Generating your skill profile…</div>;
  }
  if (error) {
    return <div style={styles.errorMessage}>{error}</div>;
  }
  if (!assessment) {
    return <div style={styles.loadingMessage}>No assessment data yet.</div>;
  }

  console.log('ASSESSMENTS', assessment);

  // Check if assessment is incomplete
  if (assessment.is_completed === 0) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            overflow: 'hidden'
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              px: 4,
              py: 5,
              textAlign: 'center',
              color: 'white'
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 80,
                height: 80,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                mb: 2,
                backdropFilter: 'blur(10px)'
              }}
            >
              <PendingIcon sx={{ fontSize: 40, color: 'white' }} />
            </Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Assessment Incomplete
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.95, maxWidth: 500, mx: 'auto' }}>
              Your assessment session ended before completion
            </Typography>
          </Box>

          <Box sx={{ px: 4, py: 5 }}>
            <Stack spacing={3}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  p: 2.5,
                  bgcolor: '#e3f2fd',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: '#90caf9'
                }}
              >
                <InfoOutlinedIcon sx={{ color: 'info.main', mt: 0.5, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600} color="text.primary" gutterBottom>
                    Why wasn't my assessment generated?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    We need more information to accurately assess your current skill level. Please engage in a longer
                    conversation with the assessment agent to help us gather sufficient insights for a comprehensive
                    evaluation.
                  </Typography>
                </Box>
              </Box>

              {onGoBack && (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    startIcon={<ArrowBackIcon />}
                    onClick={onGoBack}
                    sx={{
                      minWidth: 200,
                      fontWeight: 600,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4
                      }
                    }}
                  >
                    Go Back
                  </Button>
                </Box>
              )}
            </Stack>
          </Box>
        </Paper>
      </Container>
    );
  }

  const assessedLevel = Math.max(1, Math.min(5, assessment.assessedLevel ?? 1));
  const confidencePercent = (assessedLevel / 5) * 100;
  const confidenceLabel = assessment.levelLabel;
  const starStates = getStarStates(assessedLevel);
  const halfStarClipId = useId();

  return (
    <div style={styles.root}>
      <div style={styles.heroCard}>
        <div style={styles.heroLeft}>
          {/* <span style={styles.synthesisBadge}>SYNTHESIS COMPLETE</span> */}
          <h2 style={styles.heroTitle}>Skill Profile Interpreted</h2>
          <p style={styles.heroDesc}>
            We've analyzed your assessment responses. Here is your comprehensive skill assessment.
          </p>
        </div>
        <div style={styles.confidenceCard}>
          <div style={styles.overallScore}>
            <p style={styles.overallScoreLabel}>AI Skill Assessment</p>
            {/* <div style={styles.confidenceLabel}>Assessed Level</div>
            <div style={styles.dimensionName}>Level {assessedLevel}</div> */}
            <div style={styles.dimensionName}>
              <span style={styles.confidenceStarRow}>
                <svg width={0} height={0}>
                  <defs>
                    <clipPath id={halfStarClipId}>
                      <rect x="0" y="0" width="12" height="24" />
                    </clipPath>
                  </defs>
                </svg>
                {starStates.map((state, i) => (
                  <span
                    key={i}
                    style={{
                      color: state === 'empty' ? 'rgba(0, 210, 170, 0.35)' : '#00d2aa',
                      display: 'inline-flex'
                    }}
                  >
                    {state === 'full' && <StarFull size={25} />}
                    {state === 'half' && <StarHalf size={25} clipPathId={halfStarClipId} />}
                    {state === 'empty' && <StarEmpty size={25} />}
                  </span>
                ))}
              </span>
            </div>
          </div>

          <div style={styles.confidenceValue}>{confidenceLabel}</div>
          {/* <div style={styles.progressBarWrap}>
            <div style={{ ...styles.progressBarFill, width: `${confidencePercent}%` }} />
          </div> */}
        </div>
      </div>

      <div style={styles.twoCol}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>
            <span style={styles.cardIconGreen}>
              <CheckIcon />
            </span>
            Key Strengths
          </h3>
          {assessment.keyStrengthsSummary && <p style={styles.summaryText}>{assessment.keyStrengthsSummary}</p>}
          <ul style={styles.bulletList}>
            {assessment?.keyStrengths.map((s, i) => {
              const header = assessment.keyStrengthsHeader?.[i];
              return (
                <li key={i} style={styles.bulletItem}>
                  <span style={styles.bulletDotGreen} />
                  <span style={{ display: 'inline' }}>
                    {header !== null && header !== '' ? (
                      <>
                        <span style={{ fontWeight: 500 }}>{header}:</span> <span style={{ fontWeight: 300 }}>{s}</span>
                      </>
                    ) : (
                      <span style={{ fontWeight: 300 }}>{s}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>
            <span style={styles.cardIconGrey}>
              <TrendIcon />
            </span>
            Concept Gaps & Learning Opportunities
          </h3>
          {assessment.conceptGapsSummary && <p style={styles.summaryText}>{assessment.conceptGapsSummary}</p>}
          <div style={styles.cardContentWithButton}>
            <ul style={styles.bulletList}>
              {assessment?.conceptGaps.map((g, i) => {
                const header = assessment.conceptGapsHeader?.[i];
                return (
                  <li key={i} style={styles.bulletItem}>
                    <span style={styles.bulletDotGrey} />
                    <span style={{ display: 'inline' }}>
                      {header !== null && header !== '' ? (
                        <>
                          <span style={{ fontWeight: 500 }}>{header}:</span>{' '}
                          <span style={{ fontWeight: 300 }}>{g}</span>
                        </>
                      ) : (
                        <span style={{ fontWeight: 300 }}>{g}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
        {onViewLearningPaths && (
          <button
            type="button"
            className="learning-recommendations-btn"
            style={styles.viewPathsButton}
            onClick={onViewLearningPaths}
          >
            Learning Recommendations &gt;
          </button>
        )}
      </div>

      {serverCallId && (
        <FeedbackWidget
          feedbackId={serverCallId}
          feedbackType="assessment"
          onFeedbackSubmitted={(rating) => console.log('Feedback submitted:', rating)}
          userEmail={userEmail}
          assessmentData={assessment}
          serverCallId={serverCallId}
        />
      )}

      {/* {onStartNewInterview && (
        <div style={{ textAlign: 'center' }}>
          <button type="button" style={styles.startNewButton} onClick={onStartNewInterview}>
            Start new interview
          </button>
        </div>
      )} */}
    </div>
  );
};
